import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ContentCard from "@/components/feature/ContentCard";
import Pagination from "@/components/base/Pagination";
import { useSeo } from "@/hooks/useSeo";
import { contentItems, searchContent, type ContentType } from "@/mocks/content";

const PAGE_SIZE = 6;

const typeFilterOptions: { key: "all" | ContentType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "film", label: "影视" },
  { key: "music", label: "音乐" },
  { key: "news", label: "资讯" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState<"all" | ContentType>("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useSeo({
    title: keyword ? `“${keyword}”搜索结果 - 快猫` : "搜索结果 - 快猫",
    description: keyword
      ? `快猫为你找到与“${keyword}”相关的影视、音乐与资讯内容，支持多维筛选与分页浏览。`
      : "在快猫搜索影视、音乐与娱乐资讯，快速获取你感兴趣的内容。",
    keywords: `快猫搜索,${keyword},娱乐内容搜索,影视搜索,音乐搜索`,
  });

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  useEffect(() => {
    if (!keyword) return;
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, [keyword, typeFilter]);

  const allResults = useMemo(() => (keyword ? searchContent(keyword) : []), [keyword]);

  const results = useMemo(() => {
    if (typeFilter === "all") return allResults;
    return allResults.filter((i) => i.type === typeFilter);
  }, [allResults, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const recommendations = useMemo(
    () => [...contentItems].sort((a, b) => b.heat - a.heat).slice(0, 4),
    [],
  );

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    if (!kw) return;
    setSearchParams({ q: kw });
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
        <header>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            内容搜索
          </h1>
          <form onSubmit={submitSearch} className="mt-4 flex max-w-2xl flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索影视、音乐或资讯关键词"
                className="w-full rounded-full border border-background-300/70 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 font-label text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </header>

        {!keyword ? (
          <div className="mt-10">
            <p className="text-sm text-foreground-600">输入关键词开始搜索，试试这些热门内容：</p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {recommendations.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[240px_1fr]">
            <aside className="flex flex-col gap-4">
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-4">
                <p className="text-sm text-foreground-600">
                  关键词：<strong className="text-foreground-950">{keyword}</strong>
                </p>
                <p className="mt-2 rounded-md bg-primary-100/70 px-3 py-2 text-sm font-medium text-primary-700">
                  共找到 {results.length} 条结果
                </p>
              </div>
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-4">
                <span className="font-label text-sm font-medium text-foreground-500">类型筛选</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {typeFilterOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => { setTypeFilter(opt.key); setPage(1); }}
                      className={`whitespace-nowrap rounded-full px-3 py-1.5 font-label text-sm transition-colors ${
                        typeFilter === opt.key
                          ? "bg-accent-500 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <div>
              {loading ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-72 animate-pulse rounded-lg bg-background-100" />
                  ))}
                </div>
              ) : results.length === 0 ? (
                <div>
                  <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 py-14 text-center">
                    <i className="ri-search-eye-line text-5xl text-foreground-300" />
                    <h2 className="mt-4 font-heading text-lg font-bold text-foreground-800">
                      没有找到与“{keyword}”相关的内容
                    </h2>
                    <p className="mt-1 text-sm text-foreground-600">换个关键词试试，或看看下面这些热门推荐。</p>
                  </div>
                  <div className="mt-8">
                    <h3 className="mb-4 font-heading text-lg font-extrabold text-foreground-950">热门推荐</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      {recommendations.map((item) => (
                        <ContentCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className={typeFilter === "news" ? "flex flex-col gap-4" : "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"}>
                    {pageItems.map((item) => (
                      <ContentCard key={item.id} item={item} />
                    ))}
                  </div>
                  <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}